import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { fetchPlatforms } from '../services/configuration.service';
import type { ConfigurationPlatform } from '../types/configuration.types';

/**
 * Resolves a platform name (e.g. `'GMC'`) to the UUID the agentic-config
 * service expects in the `X-Platform-ID` header. The catalogue is fetched
 * once per session and cached forever — platforms change at deploy cadence,
 * not session, so refetches would just be noise.
 *
 * Returns `undefined` while loading or when the requested name isn't
 * registered upstream; consumers gate their dependent queries with
 * `enabled: !!platformId` so we never send a stale or missing header.
 */
export function usePlatformId(name: ConfigurationPlatform): string | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.configuration.platforms(),
    queryFn: fetchPlatforms,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data?.find((p) => p.name === name)?.id;
}
